import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { RouterLink } from '@angular/router';
import { LoaderCircle, type LucideIconData } from 'lucide-angular';
import type { Subscription } from 'rxjs';

import type { PerronCoinsMeSummary, PerronPointsEntry } from '../../../../shared/models/perron-points.model';
import { AuthService } from '../../../../shared/services/auth.service';
import { PerronPointsModalService } from '../../../../shared/services/perron-points-modal.service';
import { PointsService } from '../../../../shared/services/points.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { ButtonComponent, IconComponent } from '../../../../shared/ui';
import { formatCrc, formatInteger } from '../../../../shared/utils/currency.util';
import { ProfileCoinsService } from '../../data/profile-coins.service';
import { PROFILE_COPY } from '../../profile.copy';
import { formatIsoDateTimeAsDate } from '../../utils/profile-format.util';
import { ProfileQrComponent } from '../profile-qr/profile-qr.component';

type CoinsState = { status: 'loading' } | { status: 'error' } | { status: 'loaded'; summary: PerronCoinsMeSummary };

const COPY = PROFILE_COPY.COINS;

/** `'{n} Coin'` / `'{n} Coins'` con el punto de miles: los Coins no son dinero (`formatInteger`, W4). */
function coinsLabel(n: number): string {
  return (n === 1 ? COPY.BALANCE_ONE : COPY.BALANCE_OTHER).replace('{n}', formatInteger(n));
}

/** Etiqueta del tipo; uno que la web aún no conoce (o una clave heredada como `constructor`) → `TYPE_FALLBACK`. */
function typeLabel(type: string): string {
  return Object.hasOwn(COPY.TYPES, type) ? COPY.TYPES[type as keyof typeof COPY.TYPES] : COPY.TYPE_FALLBACK;
}

/** Importe con signo: `+n` crédito, `−n` (signo menos U+2212) débito, que ya llega negativo de la API. */
function signedAmount(amount: number): string {
  return `${amount < 0 ? '−' : '+'}${formatInteger(Math.abs(amount))}`;
}

/** `'Pedido #{n}'` con el id tal cual (sin punto de miles: es un número de pedido). */
function orderRef(preorderId: number): string {
  return COPY.ORDER_REF.replace('{n}', String(preorderId));
}

/**
 * «Mis Coins» (`/perfil/coins`, A0·web de Perrón Coins, W11): saldo, ganados en total, estado VIP, valor FUTURO del
 * Coin, canje con el modal existente y los movimientos del ledger, paginados con «Ver más movimientos». Arriba del
 * todo, fuera de los estados del resumen, «Mi QR Perrón» (A1·web, Q5): un cliente sin movimientos gana sus primeros
 * Coins justo enseñándolo en caja, así que ni la carga, ni el error ni el vacío lo esconden.
 *
 * **El estado pertenece a la cuenta** (A1·web, Q6, patrón de `ProfileOrdersComponent`): un efecto sobre
 * `currentUser()?.id` carga con el primer id y, si cambia a otro, vuelve al esqueleto y vacía los movimientos antes de
 * pedir la página 1 de la cuenta nueva. Sin id no se pide nada.
 *
 * Estados como «Mis pedidos»: esqueleto mientras carga, error con «Reintentar», vacío con la mascota. Las páginas
 * siguientes se AÑADEN sin duplicar ids. Tras un canje, `PointsService` recarga el saldo global: un efecto sobre
 * `balance()` que ignora su primera lectura vuelve a pedir la página 1 cuando ese saldo difiere del pintado.
 */
@Component({
  selector: 'app-profile-coins',
  imports: [ButtonComponent, IconComponent, NgOptimizedImage, ProfileQrComponent, RouterLink],
  templateUrl: './profile-coins.component.html',
  styleUrl: './profile-coins.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileCoinsComponent {
  private readonly coins = inject(ProfileCoinsService);
  private readonly redeemModal = inject(PerronPointsModalService);
  private readonly points = inject(PointsService);
  private readonly toast = inject(ToastService);
  private readonly currentUser = inject(AuthService).currentUser;

  protected readonly copy = COPY;
  protected readonly spinnerIcon: LucideIconData = LoaderCircle;
  protected readonly formatDate = formatIsoDateTimeAsDate;
  protected readonly typeLabel = typeLabel;
  protected readonly signedAmount = signedAmount;
  protected readonly orderRef = orderRef;

  protected readonly state = signal<CoinsState>({ status: 'loading' });
  protected readonly entries = signal<PerronPointsEntry[]>([]);
  protected readonly loadingMore = signal(false);

  private request: Subscription | null = null;
  private moreRequest: Subscription | null = null;

  protected readonly summary = computed(() => {
    const state = this.state();
    return state.status === 'loaded' ? state.summary : null;
  });

  protected readonly balanceLabel = computed(() => coinsLabel(this.summary()?.balance ?? 0));
  protected readonly lifetimeLabel = computed(() => coinsLabel(this.summary()?.lifetime_earned ?? 0));

  protected readonly vipText = computed(() => {
    const summary = this.summary();
    if (!summary) return '';
    if (summary.early_access) return COPY.VIP_ACTIVE;
    const missing = Math.max(0, summary.threshold - summary.balance);
    return missing === 1 ? COPY.VIP_PENDING_ONE : COPY.VIP_PENDING.replace('{n}', formatInteger(missing));
  });

  /** Valor en futuro (W5); sin un entero > 0 en `coin_value_crc`, no hay línea. */
  protected readonly valueText = computed(() => {
    const value = this.summary()?.coin_value_crc;
    return typeof value === 'number' && Number.isInteger(value) && value > 0
      ? COPY.VALUE.replace('{valor}', formatCrc(value))
      : null;
  });

  protected readonly hasMore = computed(() => {
    const entries = this.summary()?.entries;
    return !!entries && entries.current_page < entries.last_page;
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.request?.unsubscribe();
      this.moreRequest?.unsubscribe();
    });

    let loadedFor: number | null = null;
    effect(() => {
      const userId = this.currentUser()?.id ?? null;
      untracked(() => {
        if (userId === null) {
          // El contenedor ya quita el outlet sin sesión; por si acaso, nada en vuelo y nada pintado.
          loadedFor = null;
          this.clear();
          return;
        }
        if (userId === loadedFor) return;
        loadedFor = userId;
        this.loadFirstPage();
      });
    });

    let firstRead = true;
    effect(() => {
      const balance = this.points.balance();
      if (firstRead) {
        firstRead = false;
        return;
      }
      const painted = untracked(this.summary);
      if (painted && painted.balance !== balance) this.loadFirstPage({ silent: true });
    });
  }

  protected retry(): void {
    this.loadFirstPage();
  }

  protected openRedeem(): void {
    this.redeemModal.open();
  }

  /** Mientras carga, el botón no se desactiva (sigue a fuerza completa con su spinner): el clic se ignora aquí. */
  protected loadMore(): void {
    const entries = this.summary()?.entries;
    if (this.loadingMore() || !entries || entries.current_page >= entries.last_page) return;

    this.loadingMore.set(true);
    this.moreRequest = this.coins.page(entries.current_page + 1).subscribe({
      next: (summary) => {
        const seen = new Set(this.entries().map((entry) => entry.id));
        this.entries.update((current) => [...current, ...summary.entries.data.filter((e) => !seen.has(e.id))]);
        this.state.set({ status: 'loaded', summary });
        this.loadingMore.set(false);
      },
      // Lo pintado se queda y el botón sigue ahí para reintentar.
      error: () => {
        this.loadingMore.set(false);
        this.toast.show({ type: 'error', message: COPY.LOAD_MORE_ERROR });
      },
    });
  }

  /**
   * Pide la página 1 y sustituye lo pintado. Sin `silent` vuelve al esqueleto y vacía los movimientos antes de pedir
   * (cambio de cuenta, reintento). `silent` (la recarga tras un canje) no toca lo pintado y, si falla, lo deja.
   */
  private loadFirstPage({ silent = false } = {}): void {
    if (silent) {
      this.request?.unsubscribe();
      this.moreRequest?.unsubscribe();
      this.loadingMore.set(false);
    } else {
      this.clear();
    }

    this.request = this.coins.page(1).subscribe({
      next: (summary) => {
        this.entries.set(summary.entries.data);
        this.state.set({ status: 'loaded', summary });
      },
      error: () => {
        if (!silent) this.state.set({ status: 'error' });
      },
    });
  }

  /** Nada en vuelo, esqueleto y sin movimientos. */
  private clear(): void {
    this.request?.unsubscribe();
    this.moreRequest?.unsubscribe();
    this.loadingMore.set(false);
    this.entries.set([]);
    this.state.set({ status: 'loading' });
  }
}
